<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useQaPermissions } from 'dashboard/composables/useQaPermissions';
import { useQaRowAction } from 'dashboard/composables/useQaRowAction';
import { useAccountTimezone } from 'dashboard/composables/useAccountTimezone';
import { useStore } from 'dashboard/composables/store';
import QaDashboardApi from 'dashboard/api/qaDashboard';
// The conversations#show endpoint this used to call returns "preview messages" —
// the one-line teaser built for the conversation LIST. On a 434-message chat it
// handed back a single message, so the panel showed one line of a long dialogue.
// The messages endpoint is the one the real chat screen reads, behind the same
// authorization (inbox policy + PermissionFilterService).
import MessageApi from 'dashboard/api/inbox/message';
import QaDateRangeSelect from '../components/QaDateRangeSelect.vue';
import {
  canTransition,
  verdictLabel,
  reviewStateLabel,
  sourceLabel,
  reviewOutcomeTone,
  incidentHeadline,
} from '../helper/incidentReview';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import '../styles/qaShell.css';

// QA_FE_ROW_ACTION_V1 + QA_FE_LIVE_I18N_V1 + QA_INCIDENT_INBOX_LAYOUT_V1
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const showAlert = useAlert;
const { canReview } = useQaPermissions();
const { accountTimezone } = useAccountTimezone();
const { anySaving, isSaving, runRow, rowErrors } = useQaRowAction();
const store = useStore();

// Dropdown data for the Inbox/Agent filters. Both getters are populated by
// the dispatches in onMounted; until then the selects just show "All …".
const inboxOptions = computed(() => store.getters['inboxes/getInboxes'] || []);
const agentOptions = computed(() => store.getters['agents/getAgents'] || []);

const TABS = computed(() => [
  // The three states of one review are kept together and ahead of the
  // verdict-type tabs. Reviewed lumps Passed and Confirmed into one number, and
  // those mean opposite things — Passed says the detector was wrong and lifts
  // the agent's rate, Confirmed says the miss was real and holds it down. Since
  // a verdict now moves the KPI, "which cases did we pass" is a question worth
  // being able to ask. Both filters and both counts already existed server-side.
  { key: 'open', label: t('QA_DASHBOARD.LIVE_MONITOR.TAB_OPEN') },
  { key: 'passed', label: t('QA_DASHBOARD.LIVE_MONITOR.TAB_PASSED') },
  { key: 'confirmed', label: t('QA_DASHBOARD.LIVE_MONITOR.TAB_CONFIRMED') },
  { key: 'incorrect', label: t('QA_DASHBOARD.LIVE_MONITOR.TAB_INCORRECT') },
  { key: 'no_reply', label: t('QA_DASHBOARD.LIVE_MONITOR.TAB_NO_REPLY') },
  { key: 'correct', label: t('QA_DASHBOARD.LIVE_MONITOR.TAB_CORRECT') },
  { key: 'ambiguous', label: t('QA_DASHBOARD.LIVE_MONITOR.TAB_AMBIGUOUS') },
  {
    key: 'config_errors',
    label: t('QA_DASHBOARD.LIVE_MONITOR.TAB_CONFIG_ERRORS'),
  },
  { key: 'reviewed', label: t('QA_DASHBOARD.LIVE_MONITOR.TAB_REVIEWED') },
  { key: 'overdue', label: t('QA_DASHBOARD.LIVE_MONITOR.TAB_OVERDUE') },
  { key: 'all', label: t('QA_DASHBOARD.LIVE_MONITOR.TAB_ALL') },
]);

const SAVED_VIEWS_KEY = 'qa_live_monitor_saved_views';

const isListLoading = ref(false);
const isDetailLoading = ref(false);
const isMessagesLoading = ref(false);
const isReviewing = computed(() => anySaving.value);

const incidents = ref([]);
const selectedId = ref(null);
const detail = ref(null);
const messages = ref([]);
const tabCounts = reactive({
  open: 0,
  passed: 0,
  confirmed: 0,
  incorrect: 0,
  no_reply: 0,
  correct: 0,
  ambiguous: 0,
  config_errors: 0,
  reviewed: 0,
  all: 0,
  overdue: 0,
});
const pagination = reactive({
  page: 1,
  perPage: 30,
  totalPages: 1,
  totalCount: 0,
});
const selectedIds = ref([]);
const reviewNote = ref('');
const savedViews = ref([]);
const newViewName = ref('');

const filters = reactive({
  tab: 'open',
  source_type: 'template_audit',
  severity: '',
  inbox_id: '',
  // '' = all, 'none' = incidents with no agent attached (e.g. no-reply cases),
  // otherwise a user id as string.
  agent_user_id: '',
  q: '',
  // Whole-day window, carried in from Agent KPI so a card that says "8 misses"
  // opens exactly those 8. Empty means the whole queue, which is the normal way
  // this page is used.
  from: '',
  to: '',
});

// The day "today" means in the account's reporting timezone. The queue is
// windowed on detected_at by that clock server-side, so anchoring the presets on
// the reader's own would hand back a different set of days than the one the
// picker names — most visibly either side of midnight.
const reportingToday = computed(() => {
  const zone = accountTimezone.value;
  if (!zone) return '';
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return '';
  }
});

// A review button is only enabled when the backend state machine actually allows
// that transition from the current review_state (helper/incidentReview.js mirrors
// Qa::Incidents::ReviewService::TRANSITIONS). The backend still enforces it; this
// just stops a reviewer from clicking a button that would only bounce with an error.
const canReviewTo = toState =>
  Boolean(detail.value) && canTransition(detail.value.review_state, toState);

// The most recent human review (max created_at), surfaced under the transcript so a
// reviewer sees "what was decided and why" without opening the Audit timeline.
const latestReview = computed(() => {
  const reviews = Array.isArray(detail.value?.reviews)
    ? detail.value.reviews
    : [];
  if (!reviews.length) return null;
  return reviews.reduce((latest, row) =>
    new Date(row.created_at || 0) >= new Date(latest.created_at || 0)
      ? row
      : latest
  );
});

// Deep-link to the real conversation view so a reviewer can jump in and reply to
// the customer on the offending admin's behalf. Opens in a new tab to keep the Live
// Monitor queue in place. Whether the reviewer can actually reply is governed by
// their existing role/inbox access — this only navigates.
const conversationLink = computed(() => {
  const id = detail.value?.conversation_display_id;
  return id
    ? frontendURL(conversationUrl({ accountId: route.params.accountId, id }))
    : null;
});

const highlightCustomerIds = computed(
  () =>
    new Set((detail.value?.highlight?.customer_message_ids || []).map(Number))
);
const highlightAgentIds = computed(
  () => new Set((detail.value?.highlight?.agent_message_ids || []).map(Number))
);
const expectedCanned = computed(
  () => detail.value?.highlight?.expected_canned || []
);
const textDiff = computed(() => detail.value?.highlight?.text_diff || null);
const actualReply = computed(() => detail.value?.highlight?.actual_reply || '');
const actualMediaFingerprints = computed(() =>
  Array.isArray(detail.value?.highlight?.media_fingerprints)
    ? detail.value.highlight.media_fingerprints
    : []
);
const semanticEquivalence = computed(() => {
  const fromHighlight = detail.value?.highlight?.semantic_equivalence;
  if (fromHighlight && typeof fromHighlight === 'object') return fromHighlight;
  const fromActual = detail.value?.actual_payload?.semantic_equivalence;
  return fromActual && typeof fromActual === 'object' ? fromActual : null;
});
const expectedMediaFingerprints = computed(() => {
  const fromHighlight = detail.value?.highlight?.expected_media_fingerprints;
  if (Array.isArray(fromHighlight) && fromHighlight.length)
    return fromHighlight;
  return expectedCanned.value.flatMap(snap =>
    Array.isArray(snap?.media_fingerprints) ? snap.media_fingerprints : []
  );
});

const timelineItems = computed(() => {
  const events = (detail.value?.audit_events || []).map(event => ({
    key: `e-${event.id}`,
    at: event.created_at,
    title: event.event_type || 'event',
    detail: [
      event.actor_type && event.actor_id
        ? `${event.actor_type}#${event.actor_id}`
        : null,
      event.payload?.summary || event.payload?.note || event.payload?.reason,
    ]
      .filter(Boolean)
      .join(' · '),
  }));
  const reviews = (detail.value?.reviews || []).map(review => ({
    key: `r-${review.id}`,
    at: review.created_at,
    title: `${review.from_state || '—'} → ${review.to_state}`,
    detail: [
      review.reviewer_id ? `reviewer#${review.reviewer_id}` : null,
      review.note,
      review.human_verdict,
    ]
      .filter(Boolean)
      .join(' · '),
  }));
  return [...events, ...reviews].sort((a, b) => {
    const ta = new Date(a.at || 0).getTime();
    const tb = new Date(b.at || 0).getTime();
    return ta - tb;
  });
});

const fingerprintLabel = fingerprint => {
  if (!fingerprint || typeof fingerprint !== 'object')
    return String(fingerprint || '—');
  const name = fingerprint.filename || fingerprint.content_type || 'attachment';
  const size = Number(fingerprint.byte_size || 0);
  const sizeLabel =
    size > 0 ? ` · ${Math.max(1, Math.round(size / 1024))} KB` : '';
  return `${name}${sizeLabel}`;
};

const messageRoleLabel = message => {
  const id = Number(message.id);
  if (highlightCustomerIds.value.has(id))
    return t('QA_DASHBOARD.LIVE_MONITOR.HIGHLIGHT_CUSTOMER');
  if (highlightAgentIds.value.has(id))
    return t('QA_DASHBOARD.LIVE_MONITOR.HIGHLIGHT_AGENT');
  return '';
};

const selectedIncident = computed(() =>
  incidents.value.find(item => String(item.id) === String(selectedId.value))
);

const syncUrl = () => {
  const query = {
    tab: filters.tab || undefined,
    source_type: filters.source_type || undefined,
    severity: filters.severity || undefined,
    inbox_id: filters.inbox_id || undefined,
    agent_user_id: filters.agent_user_id || undefined,
    q: filters.q || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    page: pagination.page > 1 ? String(pagination.page) : undefined,
  };
  Object.keys(query).forEach(key => {
    if (query[key] === undefined) delete query[key];
  });
  router.replace({
    name: selectedId.value ? 'qa_monitor_incident' : 'qa_monitor_incidents',
    params: selectedId.value
      ? { ...route.params, incidentId: String(selectedId.value) }
      : { accountId: route.params.accountId },
    query,
  });
};

const readFiltersFromUrl = () => {
  filters.tab = route.query.tab || 'open';
  // P1-TPL-021/023: Template Audit is the default Live Monitor lens.
  filters.source_type =
    route.query.source_type === undefined || route.query.source_type === null
      ? 'template_audit'
      : String(route.query.source_type);
  filters.severity = route.query.severity || '';
  filters.inbox_id = route.query.inbox_id || '';
  filters.agent_user_id = route.query.agent_user_id || '';
  filters.q = route.query.q || '';
  filters.from = route.query.from || '';
  filters.to = route.query.to || '';
  pagination.page = Number(route.query.page || 1) || 1;
  if (route.params.incidentId) {
    selectedId.value = String(route.params.incidentId);
  }
};

const buildListParams = () => {
  const params = {
    tab: filters.tab,
    page: pagination.page,
    per_page: pagination.perPage,
  };
  if (filters.source_type) params.source_type = filters.source_type;
  if (filters.severity) params.severity = filters.severity;
  if (filters.inbox_id) params.inbox_id = filters.inbox_id;
  if (filters.agent_user_id) params.agent_user_id = filters.agent_user_id;
  if (filters.q) params.q = filters.q;
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  return params;
};

const loadIncidents = async ({ preserveSelection = true } = {}) => {
  isListLoading.value = true;
  try {
    const response = await QaDashboardApi.getIncidents(buildListParams());
    incidents.value = response?.data?.items || [];
    const meta = response?.data?.meta || {};
    pagination.totalCount = meta.total_count || 0;
    pagination.totalPages = meta.total_pages || 1;
    Object.assign(tabCounts, meta.tab_counts || {});

    if (!preserveSelection || !selectedId.value) {
      selectedId.value = incidents.value[0]
        ? String(incidents.value[0].id)
        : null;
    } else {
      const exists = incidents.value.some(
        item => String(item.id) === String(selectedId.value)
      );
      if (!exists && incidents.value[0]) {
        selectedId.value = String(incidents.value[0].id);
      }
    }
    syncUrl();
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.LIVE_MONITOR.ERROR_LOAD_INCIDENTS')
    );
  } finally {
    isListLoading.value = false;
  }
};

// Two loads can be in flight at once (a review reloads the list, which moves the
// selection, which starts its own load). Without a sequence the slower response
// wins and the panel ends up describing an incident the reviewer is not looking
// at — while the buttons still act on selectedId. Last request started wins.
let detailRequestSeq = 0;

const loadDetail = async id => {
  detailRequestSeq += 1;
  const seq = detailRequestSeq;
  const isStale = () => seq !== detailRequestSeq;

  if (!id) {
    detail.value = null;
    messages.value = [];
    return;
  }
  isDetailLoading.value = true;
  try {
    const response = await QaDashboardApi.getIncident(id);
    if (isStale()) return;

    detail.value = response?.data?.item || null;
    await loadMessages(detail.value);
  } catch (error) {
    if (isStale()) return;

    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.LIVE_MONITOR.ERROR_LOAD_DETAIL')
    );
    detail.value = null;
  } finally {
    if (!isStale()) isDetailLoading.value = false;
  }
};

const ACTIVITY_MESSAGE_TYPE = 2;

const readMessages = response => {
  const payload = response?.data?.payload || response?.data || {};
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.messages)) return payload.messages;
  if (Array.isArray(payload?.payload?.messages))
    return payload.payload.messages;
  return [];
};

// Hidden per the team's call: "resolved due to inactivity", "self-assigned",
// "reopened" and label changes were 15% of one real conversation and pushed the
// actual dialogue off screen. The same history is on the Audit timeline panel.
const isActivity = message =>
  Number(message?.message_type) === ACTIVITY_MESSAGE_TYPE ||
  message?.message_type === 'activity';

// The message the incident is about. Everything is anchored on it rather than on
// the conversation's tail: on a sample of A8 incidents, 4 of 12 flagged messages
// were NOT among the newest 20, so a plain latest-page fetch would show a long
// conversation with the very message under review missing from it.
const anchorMessageId = () => {
  const ids = [
    ...highlightCustomerIds.value,
    ...highlightAgentIds.value,
  ].filter(id => Number.isFinite(id) && id > 0);
  return ids.length ? Math.min(...ids) : null;
};

let messagesRequestSeq = 0;

const loadMessages = async incident => {
  messagesRequestSeq += 1;
  const seq = messagesRequestSeq;
  const isStale = () => seq !== messagesRequestSeq;

  messages.value = [];
  if (!incident?.conversation_display_id) return;
  isMessagesLoading.value = true;
  try {
    const conversationId = incident.conversation_display_id;
    const anchor = anchorMessageId();

    // Without an anchor (detectors that flag no specific message) the newest page
    // is the right thing to show.
    const requests = anchor
      ? [
          MessageApi.getPreviousMessages({ conversationId, before: anchor }),
          MessageApi.getPreviousMessages({ conversationId, after: anchor - 1 }),
        ]
      : [MessageApi.getPreviousMessages({ conversationId })];

    const responses = await Promise.all(requests);
    if (isStale()) return;

    const byId = new Map();
    responses.forEach(response =>
      readMessages(response).forEach(message => {
        if (!isActivity(message)) byId.set(Number(message.id), message);
      })
    );
    messages.value = [...byId.values()].sort(
      (a, b) => Number(a.id) - Number(b.id)
    );
  } catch (error) {
    if (isStale()) return;

    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.LIVE_MONITOR.ERROR_LOAD_MESSAGES')
    );
  } finally {
    if (!isStale()) isMessagesLoading.value = false;
  }
};

const selectIncident = id => {
  selectedId.value = String(id);
  syncUrl();
};

const onFilterChange = async () => {
  pagination.page = 1;
  selectedIds.value = [];
  await loadIncidents({ preserveSelection: false });
};

// Empty start/end is the picker's "All dates", and the queue's normal state — an
// unset window is the whole backlog rather than a missing value to reject.
const applyWindow = async ({ start, end }) => {
  filters.from = start || '';
  filters.to = end || '';
  await onFilterChange();
};

const goPage = async page => {
  if (page < 1 || page > pagination.totalPages) return;
  pagination.page = page;
  await loadIncidents();
};

const toggleSelect = id => {
  const key = String(id);
  if (selectedIds.value.includes(key)) {
    selectedIds.value = selectedIds.value.filter(item => item !== key);
  } else {
    selectedIds.value = [...selectedIds.value, key];
  }
};

const reviewSelected = async toState => {
  if (!selectedIds.value.length) {
    showAlert(t('QA_DASHBOARD.LIVE_MONITOR.ERROR_SELECT_ONE'));
    return;
  }
  if (selectedIds.value.length > 100) {
    showAlert(t('QA_DASHBOARD.LIVE_MONITOR.ERROR_BATCH_LIMIT'));
    return;
  }
  const batchKey = `batch:${selectedIds.value.join(',')}`;
  const previousStates = Object.fromEntries(
    selectedIds.value.map(id => {
      const row = incidents.value.find(item => String(item.id) === String(id));
      return [String(id), row?.review_state];
    })
  );
  try {
    await runRow(batchKey, {
      applyOptimistic: () => {
        selectedIds.value.forEach(id => {
          const row = incidents.value.find(
            item => String(item.id) === String(id)
          );
          if (row) row.review_state = toState;
        });
      },
      execute: async () => {
        await QaDashboardApi.batchReviewIncidents({
          incident_ids: selectedIds.value,
          review_state: toState,
          review_note: reviewNote.value,
        });
        selectedIds.value = [];
        reviewNote.value = '';
        await loadIncidents();
        if (selectedId.value) await loadDetail(selectedId.value);
      },
      rollback: () => {
        Object.entries(previousStates).forEach(([id, state]) => {
          const row = incidents.value.find(
            item => String(item.id) === String(id)
          );
          if (row && state !== undefined) row.review_state = state;
        });
      },
    });
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        rowErrors.value[batchKey] ||
        t('QA_DASHBOARD.LIVE_MONITOR.ERROR_BATCH_FAILED')
    );
  }
};

const reviewCurrent = async toState => {
  if (!selectedId.value) return;
  const id = selectedId.value;
  const row = incidents.value.find(item => String(item.id) === String(id));
  const previousState = row?.review_state;
  const previousDetailState = detail.value?.review_state;
  try {
    await runRow(id, {
      applyOptimistic: () => {
        if (row) row.review_state = toState;
        if (detail.value && String(detail.value.id) === String(id)) {
          detail.value.review_state = toState;
        }
      },
      execute: async () => {
        await QaDashboardApi.updateIncident(id, {
          review_state: toState,
          review_note: reviewNote.value,
          assign_reviewer: true,
        });
        reviewNote.value = '';
        await loadIncidents();
        // loadIncidents may have moved the selection — a reviewed incident drops
        // out of the Pending review tab — and that move already loaded the right
        // detail through the selectedId watcher. Reloading `id` here regardless
        // put the previous incident back in the panel while the buttons kept
        // acting on the new selectedId, so the panel described one incident and
        // Pass/Confirm/Reopen hit another.
        if (String(selectedId.value) === String(id)) await loadDetail(id);
      },
      rollback: () => {
        if (row && previousState !== undefined)
          row.review_state = previousState;
        if (
          detail.value &&
          String(detail.value.id) === String(id) &&
          previousDetailState !== undefined
        ) {
          detail.value.review_state = previousDetailState;
        }
      },
    });
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        rowErrors.value[String(id)] ||
        t('QA_DASHBOARD.LIVE_MONITOR.ERROR_REVIEW_FAILED')
    );
  }
};

const messageClass = message => {
  const id = Number(message.id);
  if (highlightCustomerIds.value.has(id)) return 'qa-msg qa-msg--customer';
  if (highlightAgentIds.value.has(id)) return 'qa-msg qa-msg--agent';
  if (message.message_type === 0 || message.message_type === 'incoming')
    return 'qa-msg qa-msg--in';
  if (message.message_type === 1 || message.message_type === 'outgoing')
    return 'qa-msg qa-msg--out';
  return 'qa-msg';
};

const scrollToHighlightedMessages = async () => {
  await nextTick();
  const target = document.querySelector(
    '.qa-live-messages .qa-msg--customer, .qa-live-messages .qa-msg--agent'
  );
  if (target && typeof target.scrollIntoView === 'function') {
    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
};

watch(
  () => [
    messages.value.length,
    selectedId.value,
    highlightCustomerIds.value.size,
    highlightAgentIds.value.size,
  ],
  () => {
    if (messages.value.length) scrollToHighlightedMessages();
  }
);

// For the QA payload's own fields, which serialize as ISO strings.
const formatTime = value => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

// Compact stamp for the list cards ("Sep 4, 13:25"); the Evidence pane keeps
// the full formatTime rendering of the same detected_at, so the two always
// name the same moment. detected_at is the customer message that opened the
// case, i.e. when the problem happened — not when a scan noticed it.
const formatCardTime = value => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return String(value);
  }
};

const agentNameById = computed(() => {
  const map = {};
  agentOptions.value.forEach(agent => {
    map[agent.id] = agent.name || agent.email;
  });
  return map;
});

// Blame line for a card. Incidents with no agent attached (no-reply cases:
// nobody answered, so there is nobody to name) say so explicitly; an id that
// is no longer an agent in this account still renders as "Agent #id" rather
// than a blank.
const agentLabel = item => {
  if (!item.agent_user_id) return t('QA_DASHBOARD.LIVE_MONITOR.NO_AGENT');
  return (
    agentNameById.value[item.agent_user_id] || `Agent #${item.agent_user_id}`
  );
};

// Chat messages are not ISO strings: the messages API sends created_at as an
// integer of SECONDS, and new Date() reads a number as milliseconds, which is
// how every message here used to be stamped 21 Jan 1970. Read it the way the
// chat screen does, in the account's timezone so every reviewer sees one clock.
const formatMessageTime = value => {
  if (!value) return '—';
  try {
    return messageTimestamp(
      Number(value),
      'LLL d, h:mm a',
      accountTimezone.value
    );
  } catch {
    return String(value);
  }
};

const severityClass = severity => `qa-sev qa-sev--${severity || 'medium'}`;

// Hidden, not removed. The badge is the only thing on a card that says which
// detector raised the incident, which stops mattering when the Source filter is
// narrowed to one (the usual case) and starts mattering again in the All tab.
// Flip to true to bring it back.
const SHOW_SOURCE_BADGE = false;

const reviewStateChipClass = state =>
  `qa-live-state qa-live-state--${
    String(state) === 'open' ? 'pending' : reviewOutcomeTone(state)
  }`;

const loadSavedViews = () => {
  try {
    savedViews.value = JSON.parse(
      localStorage.getItem(SAVED_VIEWS_KEY) || '[]'
    );
  } catch {
    savedViews.value = [];
  }
};

const saveCurrentView = () => {
  const name = newViewName.value.trim();
  if (!name) {
    showAlert(t('QA_DASHBOARD.LIVE_MONITOR.ERROR_VIEW_NAME'));
    return;
  }
  const view = {
    id: `${Date.now()}`,
    name,
    filters: { ...filters },
  };
  savedViews.value = [view, ...savedViews.value].slice(0, 12);
  localStorage.setItem(SAVED_VIEWS_KEY, JSON.stringify(savedViews.value));
  newViewName.value = '';
};

const applySavedView = async view => {
  // Views saved before the agent filter existed have no agent_user_id key, and
  // Object.assign would then keep whatever is currently selected — reset first
  // so an old view means what it meant when it was saved.
  Object.assign(filters, { agent_user_id: '' }, view.filters || {});
  pagination.page = 1;
  await loadIncidents({ preserveSelection: false });
};

watch(selectedId, id => {
  loadDetail(id);
});

onMounted(async () => {
  readFiltersFromUrl();
  loadSavedViews();
  // Fill the Inbox/Agent filter dropdowns; fire-and-forget so the incident
  // queue itself never waits on them.
  store.dispatch('inboxes/get');
  store.dispatch('agents/get');
  await loadIncidents();
  if (selectedId.value) await loadDetail(selectedId.value);
});
</script>

<template>
  <div class="qa-live qa-page qa-page--incident">
    <header class="qa-live-header">
      <div class="qa-live-header-text">
        <h1 class="qa-live-title">
          {{ t('QA_DASHBOARD.LIVE_MONITOR.TITLE') }}
        </h1>
        <p class="qa-live-desc">
          {{ t('QA_DASHBOARD.LIVE_MONITOR.DESCRIPTION') }}
        </p>
      </div>
    </header>

    <nav class="qa-live-tabs" role="tablist" aria-label="Incident inbox tabs">
      <button
        v-for="tab in TABS"
        :key="tab.key"
        type="button"
        role="tab"
        class="qa-live-tab"
        :class="{ 'is-active': filters.tab === tab.key }"
        :aria-selected="filters.tab === tab.key"
        @click="
          filters.tab = tab.key;
          onFilterChange();
        "
      >
        {{ tab.label }}
        <span class="qa-live-tab-count">{{ tabCounts[tab.key] || 0 }}</span>
      </button>
    </nav>

    <div class="qa-live-filter-card">
      <div class="qa-live-filter-row">
        <div class="qa-live-daterange">
          <QaDateRangeSelect
            :start="filters.from"
            :end="filters.to"
            :today="reportingToday"
            allow-all-time
            @change="applyWindow"
          />
        </div>
        <label class="qa-live-field">
          <span class="qa-live-label">Source</span>
          <select
            v-model="filters.source_type"
            class="qa-live-input"
            @change="onFilterChange"
          >
            <option value="">
              {{ t('QA_DASHBOARD.LIVE_MONITOR.ALL_SOURCES') }}
            </option>
            <option value="template_audit">
              {{ t('QA_DASHBOARD.LIVE_MONITOR.SOURCE_TEMPLATE') }}
            </option>
            <option value="safety_language">
              {{ t('QA_DASHBOARD.LIVE_MONITOR.SOURCE_SAFETY') }}
            </option>
          </select>
        </label>
        <label class="qa-live-field">
          <span class="qa-live-label">Severity</span>
          <select
            v-model="filters.severity"
            class="qa-live-input"
            @change="onFilterChange"
          >
            <option value="">
              {{ t('QA_DASHBOARD.LIVE_MONITOR.ALL_SEVERITIES') }}
            </option>
            <option value="low">
              {{ t('QA_DASHBOARD.LIVE_MONITOR.SEVERITY_LOW') }}
            </option>
            <option value="medium">
              {{ t('QA_DASHBOARD.LIVE_MONITOR.SEVERITY_MEDIUM') }}
            </option>
            <option value="high">
              {{ t('QA_DASHBOARD.LIVE_MONITOR.SEVERITY_HIGH') }}
            </option>
            <option value="critical">
              {{ t('QA_DASHBOARD.LIVE_MONITOR.SEVERITY_CRITICAL') }}
            </option>
          </select>
        </label>
        <label class="qa-live-field">
          <span class="qa-live-label">Inbox</span>
          <select
            v-model="filters.inbox_id"
            class="qa-live-input"
            @change="onFilterChange"
          >
            <option value="">
              {{ t('QA_DASHBOARD.LIVE_MONITOR.ALL_INBOXES') }}
            </option>
            <option
              v-for="inbox in inboxOptions"
              :key="inbox.id"
              :value="String(inbox.id)"
            >
              {{ inbox.name }}
            </option>
          </select>
        </label>
        <label class="qa-live-field">
          <span class="qa-live-label">Agent</span>
          <select
            v-model="filters.agent_user_id"
            class="qa-live-input"
            @change="onFilterChange"
          >
            <option value="">
              {{ t('QA_DASHBOARD.LIVE_MONITOR.ALL_AGENTS') }}
            </option>
            <option value="none">
              {{ t('QA_DASHBOARD.LIVE_MONITOR.NO_AGENT') }}
            </option>
            <option
              v-for="agent in agentOptions"
              :key="agent.id"
              :value="String(agent.id)"
            >
              {{ agent.name || agent.email }}
            </option>
          </select>
        </label>
        <label class="qa-live-field qa-live-field--grow">
          <span class="qa-live-label">Search</span>
          <input
            v-model="filters.q"
            class="qa-live-input"
            :placeholder="t('QA_DASHBOARD.LIVE_MONITOR.SEARCH_PLACEHOLDER')"
            @keyup.enter="onFilterChange"
          />
        </label>
        <div class="qa-live-filter-actions">
          <button type="button" class="qa-live-btn" @click="onFilterChange">
            {{ t('QA_DASHBOARD.LIVE_MONITOR.APPLY') }}
          </button>
        </div>
      </div>

      <div v-if="savedViews.length" class="qa-live-saved">
        <span class="qa-live-saved-label">Saved views</span>
        <button
          v-for="view in savedViews"
          :key="view.id"
          type="button"
          class="qa-live-chip"
          @click="applySavedView(view)"
        >
          {{ view.name }}
        </button>
      </div>

      <details class="qa-live-disclosure qa-live-disclosure--inline">
        <summary>{{ t('QA_DASHBOARD.LIVE_MONITOR.SAVE_VIEW') }}</summary>
        <div class="qa-live-save-row">
          <input
            v-model="newViewName"
            class="qa-live-input qa-live-input--grow"
            :placeholder="t('QA_DASHBOARD.LIVE_MONITOR.SAVE_VIEW_PLACEHOLDER')"
            @keyup.enter="saveCurrentView"
          />
          <button
            type="button"
            class="qa-live-btn qa-live-btn--ghost"
            @click="saveCurrentView"
          >
            {{ t('QA_DASHBOARD.LIVE_MONITOR.SAVE_VIEW') }}
          </button>
        </div>
      </details>
    </div>

    <div class="qa-live-grid">
      <aside class="qa-live-col qa-live-col--list">
        <div class="qa-live-col-head">
          <strong>{{ t('QA_DASHBOARD.LIVE_MONITOR.INCIDENTS') }}</strong>
          <span>{{ pagination.totalCount }}</span>
        </div>
        <div v-if="isListLoading" class="qa-live-empty">
          {{ t('QA_DASHBOARD.LIVE_MONITOR.LOADING') }}
        </div>
        <div v-else-if="!incidents.length" class="qa-live-empty">
          {{ t('QA_DASHBOARD.LIVE_MONITOR.EMPTY') }}
        </div>
        <button
          v-for="item in incidents"
          :key="item.id"
          type="button"
          class="qa-live-item"
          :class="{
            'is-active': String(selectedId) === String(item.id),
            'is-overdue': item.overdue,
          }"
          @click="selectIncident(item.id)"
        >
          <div class="qa-live-item-top">
            <label class="qa-live-check" @click.stop>
              <input
                type="checkbox"
                :checked="selectedIds.includes(String(item.id))"
                @change="toggleSelect(item.id)"
              />
            </label>
            <span :class="severityClass(item.severity)">{{
              item.severity
            }}</span>
            <span :class="reviewStateChipClass(item.review_state)">
              {{ reviewStateLabel(item.review_state) }}
              <template v-if="isSaving(item.id)">
                {{ t('QA_DASHBOARD.LIVE_MONITOR.SAVING_SUFFIX') }}
              </template>
            </span>
            <span v-if="SHOW_SOURCE_BADGE" class="qa-live-item-source">{{
              sourceLabel(item.source_type)
            }}</span>
            <span class="qa-live-item-when">{{
              formatCardTime(item.detected_at)
            }}</span>
          </div>
          <div class="qa-live-item-title">{{ incidentHeadline(item) }}</div>
          <div class="qa-live-item-summary">{{ item.summary }}</div>
          <div class="qa-live-item-meta">
            <span>#{{ item.conversation_display_id || '—' }}</span>
            <span>· {{ agentLabel(item) }}</span>
          </div>
        </button>
        <div class="qa-live-pager">
          <button
            type="button"
            class="qa-live-btn qa-live-btn--ghost"
            :disabled="pagination.page <= 1"
            @click="goPage(pagination.page - 1)"
          >
            Prev
          </button>
          <span>{{ pagination.page }} / {{ pagination.totalPages }}</span>
          <button
            type="button"
            class="qa-live-btn qa-live-btn--ghost"
            :disabled="pagination.page >= pagination.totalPages"
            @click="goPage(pagination.page + 1)"
          >
            Next
          </button>
        </div>
      </aside>

      <section class="qa-live-col qa-live-col--chat">
        <div class="qa-live-col-head">
          <strong>Conversation</strong>
          <span class="qa-live-conv-head-right">
            <span v-if="detail">#{{ detail.conversation_display_id }}</span>
            <a
              v-if="conversationLink"
              :href="conversationLink"
              target="_blank"
              rel="noopener"
              class="qa-live-btn qa-live-btn--ghost qa-live-open-conv"
            >
              Open conversation ↗
            </a>
          </span>
        </div>
        <div v-if="isMessagesLoading || isDetailLoading" class="qa-live-empty">
          {{ t('QA_DASHBOARD.LIVE_MONITOR.LOADING_CONVERSATION') }}
        </div>
        <div v-else-if="!messages.length" class="qa-live-empty">
          Select an incident to preview the conversation.
        </div>
        <div v-else class="qa-live-messages">
          <div
            v-for="message in messages"
            :key="message.id"
            :class="messageClass(message)"
          >
            <div class="qa-msg-meta">
              <span>
                {{ message.sender?.name || message.message_type }}
                <em v-if="messageRoleLabel(message)" class="qa-msg-role">{{
                  messageRoleLabel(message)
                }}</em>
              </span>
              <span>{{ formatMessageTime(message.created_at) }}</span>
            </div>
            <div class="qa-msg-body">
              {{ message.content || '[attachment / empty]' }}
            </div>
          </div>
        </div>
        <div
          v-if="latestReview"
          class="qa-live-outcome"
          :class="`qa-live-outcome--${reviewOutcomeTone(latestReview.to_state)}`"
        >
          <div class="qa-live-outcome-head">
            <strong>{{ reviewStateLabel(latestReview.to_state) }}</strong>
            <span class="qa-live-outcome-time">{{
              formatTime(latestReview.created_at)
            }}</span>
          </div>
          <div v-if="latestReview.note" class="qa-live-outcome-note">
            "{{ latestReview.note }}"
          </div>
        </div>
      </section>

      <aside class="qa-live-col qa-live-col--side">
        <div class="qa-live-col-head">
          <strong>Evidence & review</strong>
        </div>
        <div v-if="!detail" class="qa-live-empty">No incident selected.</div>
        <template v-else>
          <div class="qa-live-panel qa-live-panel--status">
            <div class="qa-live-kv">
              <span>Machine</span>
              <strong>{{ verdictLabel(detail.machine_verdict) }}</strong>
            </div>
            <div class="qa-live-kv">
              <span>Human</span>
              <strong>
                {{
                  detail.human_verdict
                    ? verdictLabel(detail.human_verdict)
                    : '—'
                }}
              </strong>
            </div>
            <div class="qa-live-kv">
              <span>Review</span>
              <strong>{{ reviewStateLabel(detail.review_state) }}</strong>
            </div>
            <div class="qa-live-kv">
              <span>Detected</span>
              <strong>{{ formatTime(detail.detected_at) }}</strong>
            </div>
            <div class="qa-live-kv">
              <span>Due</span><strong>{{ formatTime(detail.due_at) }}</strong>
            </div>
            <div v-if="detail.confidence != null" class="qa-live-kv">
              <span>Confidence</span>
              <strong>{{ Number(detail.confidence).toFixed(2) }}</strong>
            </div>
          </div>

          <div v-if="canReview" class="qa-live-panel qa-live-panel--actions">
            <h3>Review</h3>
            <textarea
              v-model="reviewNote"
              class="qa-live-textarea"
              rows="2"
              placeholder="Review note (optional)"
            />
            <div class="qa-live-actions qa-live-actions--primary">
              <button
                type="button"
                class="qa-live-btn qa-live-btn--good"
                :disabled="isReviewing || !canReviewTo('passed_false_positive')"
                @click="reviewCurrent('passed_false_positive')"
              >
                <!-- "Pass" and "Confirm" are the state machine's words, not a
                     reviewer's. Both describe what the reviewer is deciding
                     about the MACHINE: pass means the flag was wrong, confirm
                     means it was right. Saying that outright removes a guess.
                     The rest of the app still says Passed/Confirmed in about
                     fifteen places; those follow page by page. -->
                Not a problem
              </button>
              <button
                type="button"
                class="qa-live-btn qa-live-btn--danger"
                :disabled="isReviewing || !canReviewTo('confirmed')"
                @click="reviewCurrent('confirmed')"
              >
                Confirmed problem
              </button>
            </div>
            <div class="qa-live-actions qa-live-actions--secondary">
              <button
                type="button"
                class="qa-live-btn qa-live-btn--ghost"
                :disabled="isReviewing || !canReviewTo('acknowledged')"
                @click="reviewCurrent('acknowledged')"
              >
                Ack
              </button>
              <button
                type="button"
                class="qa-live-btn qa-live-btn--ghost"
                :disabled="isReviewing || !canReviewTo('investigating')"
                @click="reviewCurrent('investigating')"
              >
                Investigate
              </button>
              <button
                type="button"
                class="qa-live-btn qa-live-btn--ghost"
                :disabled="isReviewing || !canReviewTo('policy_exception')"
                @click="reviewCurrent('policy_exception')"
              >
                Exception
              </button>
              <button
                type="button"
                class="qa-live-btn qa-live-btn--ghost"
                :disabled="isReviewing || !canReviewTo('open')"
                @click="reviewCurrent('open')"
              >
                Reopen
              </button>
            </div>
            <div
              v-if="selectedIds.length"
              class="qa-live-actions qa-live-actions--batch"
            >
              <button
                type="button"
                class="qa-live-btn qa-live-btn--good"
                :disabled="isReviewing"
                @click="reviewSelected('passed_false_positive')"
              >
                {{
                  t('QA_DASHBOARD.LIVE_MONITOR.BATCH_PASS', {
                    count: selectedIds.length,
                  })
                }}
              </button>
              <button
                type="button"
                class="qa-live-btn qa-live-btn--danger"
                :disabled="isReviewing"
                @click="reviewSelected('confirmed')"
              >
                {{
                  t('QA_DASHBOARD.LIVE_MONITOR.BATCH_CONFIRM', {
                    count: selectedIds.length,
                  })
                }}
              </button>
            </div>
          </div>

          <details v-if="semanticEquivalence" class="qa-live-disclosure">
            <summary>Semantic equivalence</summary>
            <div class="qa-live-panel-body">
              <div class="qa-live-kv">
                <span>Path</span>
                <strong>{{ semanticEquivalence.path || '—' }}</strong>
              </div>
              <div class="qa-live-kv">
                <span>Accepted</span>
                <strong>{{
                  semanticEquivalence.accepted
                    ? 'yes'
                    : semanticEquivalence.equivalent
                      ? 'no (below threshold)'
                      : 'no'
                }}</strong>
              </div>
              <div class="qa-live-kv">
                <span>Confidence</span>
                <strong>{{
                  semanticEquivalence.confidence != null
                    ? Number(semanticEquivalence.confidence).toFixed(2)
                    : '—'
                }}</strong>
              </div>
              <div v-if="semanticEquivalence.reason" class="qa-live-kv">
                <span>Reason</span>
                <strong>{{ semanticEquivalence.reason }}</strong>
              </div>
              <div class="qa-live-diff-block">
                <div class="qa-live-diff-label">Explanation</div>
                <pre class="qa-live-pre">{{
                  semanticEquivalence.explanation || '—'
                }}</pre>
              </div>
            </div>
          </details>

          <details class="qa-live-disclosure">
            <summary>Expected canned</summary>
            <div class="qa-live-panel-body">
              <div v-if="!expectedCanned.length" class="qa-live-muted">
                No expected canned snapshot.
              </div>
              <div
                v-for="(snap, index) in expectedCanned"
                :key="`${snap.canned_response_id || index}`"
                class="qa-live-expected-item"
              >
                <div class="qa-live-expected-head">
                  <code>{{ snap.short_code || 'canned' }}</code>
                  <span v-if="snap.group_name" class="qa-live-muted">{{
                    snap.group_name
                  }}</span>
                </div>
                <pre class="qa-live-pre">{{
                  snap.content || '(media only)'
                }}</pre>
                <div
                  v-if="
                    snap.media_attachment_count ||
                    (snap.media_fingerprints || []).length
                  "
                  class="qa-live-attach-list"
                >
                  <div class="qa-live-diff-label">
                    Attachments ({{
                      snap.media_attachment_count ||
                      (snap.media_fingerprints || []).length
                    }})
                  </div>
                  <div
                    v-for="(fp, fpIndex) in snap.media_fingerprints || []"
                    :key="`exp-snap-${index}-${fpIndex}`"
                    class="qa-live-attach-item"
                  >
                    {{ fingerprintLabel(fp) }}
                  </div>
                </div>
              </div>
            </div>
          </details>

          <details class="qa-live-disclosure">
            <summary>Expected / actual diff</summary>
            <div class="qa-live-panel-body">
              <div v-if="!textDiff && !actualReply" class="qa-live-muted">
                No reply comparison yet.
              </div>
              <template v-else>
                <div class="qa-live-kv">
                  <span>Equal</span>
                  <strong>{{ textDiff?.equal ? 'yes' : 'no' }}</strong>
                </div>
                <div class="qa-live-diff-block">
                  <div class="qa-live-diff-label">Expected (normalized)</div>
                  <pre class="qa-live-pre">{{
                    textDiff?.expected_normalized ||
                    expectedCanned[0]?.content ||
                    '—'
                  }}</pre>
                </div>
                <div class="qa-live-diff-block">
                  <div class="qa-live-diff-label">Actual reply</div>
                  <pre class="qa-live-pre">{{ actualReply || '—' }}</pre>
                </div>
                <div class="qa-live-diff-block">
                  <div class="qa-live-diff-label">Actual (normalized)</div>
                  <pre class="qa-live-pre">{{
                    textDiff?.actual_normalized || actualReply || '—'
                  }}</pre>
                </div>
              </template>
            </div>
          </details>

          <details class="qa-live-disclosure">
            <summary>
              Attachments ({{ expectedMediaFingerprints.length }} /
              {{ actualMediaFingerprints.length }})
            </summary>
            <div class="qa-live-panel-body">
              <div class="qa-live-diff-block">
                <div class="qa-live-diff-label">
                  Expected ({{ expectedMediaFingerprints.length }})
                </div>
                <div
                  v-if="!expectedMediaFingerprints.length"
                  class="qa-live-muted"
                >
                  None
                </div>
                <div
                  v-for="(fp, index) in expectedMediaFingerprints"
                  :key="`exp-fp-${index}`"
                  class="qa-live-attach-item"
                >
                  {{ fingerprintLabel(fp) }}
                </div>
              </div>
              <div class="qa-live-diff-block">
                <div class="qa-live-diff-label">
                  Actual ({{ actualMediaFingerprints.length }})
                </div>
                <div
                  v-if="!actualMediaFingerprints.length"
                  class="qa-live-muted"
                >
                  None
                </div>
                <div
                  v-for="(fp, index) in actualMediaFingerprints"
                  :key="`act-fp-${index}`"
                  class="qa-live-attach-item"
                >
                  {{ fingerprintLabel(fp) }}
                </div>
              </div>
            </div>
          </details>

          <details class="qa-live-disclosure">
            <summary>Detector evidence</summary>
            <div class="qa-live-panel-body">
              <pre class="qa-live-pre">{{
                JSON.stringify(detail.evidence || {}, null, 2)
              }}</pre>
            </div>
          </details>

          <details class="qa-live-disclosure">
            <summary>
              Telegram ({{ (detail.telegram_deliveries || []).length }})
            </summary>
            <div class="qa-live-panel-body">
              <div
                v-if="!(detail.telegram_deliveries || []).length"
                class="qa-live-muted"
              >
                No deliveries yet.
              </div>
              <div
                v-for="row in detail.telegram_deliveries || []"
                :key="row.id"
                class="qa-live-telegram"
              >
                <span :class="`qa-badge-status qa-badge-status--${row.status}`">
                  {{ row.status }}
                </span>
                <span>{{ row.destination_id }}</span>
                <span>try {{ row.attempt_count || 0 }}</span>
              </div>
            </div>
          </details>

          <details class="qa-live-disclosure">
            <summary>Audit timeline ({{ timelineItems.length }})</summary>
            <div class="qa-live-panel-body">
              <div v-if="!timelineItems.length" class="qa-live-muted">
                No review history yet.
              </div>
              <div
                v-for="item in timelineItems"
                :key="item.key"
                class="qa-live-timeline"
              >
                <strong>{{ item.title }}</strong>
                <span v-if="item.detail">{{ item.detail }}</span>
                <span>{{ formatTime(item.at) }}</span>
              </div>
            </div>
          </details>
        </template>
      </aside>
    </div>
  </div>
</template>

<style scoped>
/* QA_INCIDENT_INBOX_LAYOUT_V1 */
.qa-live {
  /* Transcript bubble fills. These were literals on the bubble rules, which is
     why they survived every theme: dark forces .qa-msg-body to white and the
     customer bubble stayed cream, so the triggering message — the one thing a
     reviewer opens the panel to read — was white on cream. As tokens they get a
     dark counterpart below. Light and Mix keep the values they always had. */
  --qa-msg-customer-bg: #fffbeb;
  --qa-msg-out-bg: #eff6ff;

  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: radial-gradient(
      1200px 420px at 10% -10%,
      rgba(14, 116, 144, 0.06),
      transparent 60%
    ),
    linear-gradient(180deg, var(--qa-muted) 0%, var(--qa-bg) 42%);
  color: var(--qa-text);
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid var(--qa-border);
}

.qa-live-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-end;
  padding: 14px 20px 12px;
  border-bottom: 1px solid var(--qa-border);
  background: var(--qa-surface);
}

.qa-live-header-text {
  min-width: 0;
}

.qa-live-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--qa-text);
}

.qa-live-desc {
  margin: 4px 0 0;
  color: var(--qa-text-muted);
  font-size: 13px;
  line-height: 1.4;
}

.qa-live-header-actions,
.qa-live-filter-actions,
.qa-live-actions,
.qa-live-pager,
.qa-live-item-top,
.qa-live-item-meta,
.qa-live-saved,
.qa-live-telegram,
.qa-live-timeline,
.qa-live-kv,
.qa-live-save-row,
.qa-msg-meta {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.qa-live-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  padding: 0 16px;
  background: var(--qa-surface);
  border-bottom: 1px solid var(--qa-border-soft);
}

.qa-live-tab {
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  border-radius: 0;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: var(--qa-text-muted);
}

.qa-live-tab:hover {
  color: var(--qa-text);
}

.qa-live-tab.is-active {
  color: var(--qa-text);
  border-bottom-color: var(--qa-text);
  background: transparent;
}

.qa-live-tab-count {
  margin-left: 4px;
  opacity: 0.7;
  font-weight: 500;
}

.qa-live-filter-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 20px 14px;
  background: var(--qa-surface);
  border-bottom: 1px solid var(--qa-border);
}

.qa-live-filter-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.qa-live-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 140px;
}

.qa-live-field--grow {
  flex: 1 1 220px;
  min-width: 180px;
}

.qa-live-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--qa-text-muted);
}

/* The picker labels itself, so it sits on the row's baseline beside the
   labelled selects rather than under a heading of its own. */
.qa-live-daterange,
.qa-live-filter-actions {
  padding-bottom: 1px;
}

.qa-live-saved-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--qa-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.qa-live-input,
.qa-live-textarea {
  border: 1px solid var(--qa-border);
  border-radius: 8px;
  padding: 8px 10px;
  background: var(--qa-input-bg, var(--qa-surface));
  color: var(--qa-text);
  font-size: 13px;
}

.qa-live-input--grow {
  flex: 1;
  min-width: 160px;
}

.qa-live-textarea {
  width: 100%;
  resize: vertical;
  margin-bottom: 8px;
}

.qa-live-btn {
  border: 1px solid var(--qa-accent);
  background: var(--qa-accent);
  color: #fff;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.qa-live-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.qa-live-btn--ghost {
  background: transparent;
  color: var(--qa-text-secondary);
  border-color: var(--qa-border);
}

.qa-live-btn--ghost:hover {
  background: var(--qa-muted);
  color: var(--qa-text);
}

.qa-live-btn--good {
  background: var(--qa-good);
  border-color: var(--qa-good);
}

.qa-live-btn--danger {
  background: var(--qa-bad);
  border-color: var(--qa-bad);
}

.qa-live-chip {
  border: 1px solid var(--qa-border);
  background: var(--qa-muted);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  color: var(--qa-text-secondary);
}

.qa-live-chip:hover {
  border-color: var(--qa-accent);
  color: var(--qa-text);
}

.qa-live-disclosure {
  border: 1px solid var(--qa-border-soft);
  border-radius: 8px;
  background: var(--qa-muted);
  margin: 8px 10px;
  padding: 0 12px;
}

.qa-live-disclosure--inline {
  margin: 0;
}

.qa-live-disclosure > summary {
  cursor: pointer;
  list-style: none;
  padding: 10px 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--qa-text-secondary);
}

.qa-live-disclosure > summary::-webkit-details-marker {
  display: none;
}

.qa-live-disclosure[open] {
  padding-bottom: 10px;
}

.qa-live-panel-body {
  padding-bottom: 4px;
}

.qa-live-save-row {
  width: 100%;
}

.qa-live-grid {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(260px, 0.85fr) minmax(340px, 1.35fr) minmax(
      300px,
      1fr
    );
  gap: 0;
}

.qa-live-col {
  min-height: 0;
  overflow: auto;
  background: var(--qa-surface);
  border-right: 1px solid var(--qa-border);
}

.qa-live-col--side {
  border-right: 0;
}

.qa-live-col-head {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  padding: 11px 14px;
  background: var(--qa-muted);
  border-bottom: 1px solid var(--qa-border);
  font-size: 13px;
  color: var(--qa-text);
}

.qa-live-conv-head-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.qa-live-open-conv {
  text-decoration: none;
}

.qa-live-item {
  display: block;
  /* Buttons shrink-to-fit even at display:block, so with short incident
     summaries the cards collapsed to their text width and left a blank
     strip beside the list (reported from chatsystem, 2026-09-06). Fill
     the column instead: 100% minus the 10px side margins below. */
  width: calc(100% - 20px);
  text-align: left;
  border: 1px solid var(--qa-border);
  border-radius: 10px;
  background: var(--qa-surface);
  color: var(--qa-text);
  padding: 11px 13px;
  margin: 8px 10px 0;
  cursor: pointer;
  transition:
    box-shadow 0.15s,
    border-color 0.15s;
}

.qa-live-item:last-of-type {
  margin-bottom: 8px;
}

.qa-live-item:hover {
  border-color: var(--qa-accent);
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
}

.qa-live-item.is-active {
  background: #ecfeff;
  border-color: var(--qa-accent);
  box-shadow:
    0 0 0 1px var(--qa-accent),
    0 2px 10px rgba(15, 23, 42, 0.08);
}

.qa-live-item.is-overdue .qa-live-item-title {
  color: var(--qa-bad);
}

/* Now carries the machine's finding rather than a restatement of the badge, so
   it is the line a reviewer should land on first. */
.qa-live-item-title {
  font-weight: 700;
  font-size: 14px;
  margin: 6px 0 4px;
  color: var(--qa-text);
}

.qa-live-item-summary,
.qa-live-item-meta,
.qa-live-muted,
.qa-live-empty {
  color: var(--qa-text-muted);
  font-size: 12px;
}

/* Detected-at stamp in the card's top row; auto margin sends it to the right
   edge, into the gap after the severity/state chips. */
.qa-live-item-when {
  margin-left: auto;
  color: var(--qa-text-muted);
  font-size: 11px;
  white-space: nowrap;
}

.qa-live-item-summary {
  margin-top: 2px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.qa-live-item-source {
  font-size: 11px;
  color: var(--qa-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.qa-live-empty {
  padding: 24px 16px;
}

.qa-live-pager {
  justify-content: space-between;
  padding: 12px;
  position: sticky;
  bottom: 0;
  background: var(--qa-surface);
  border-top: 1px solid var(--qa-border);
  color: var(--qa-text-muted);
}

.qa-sev {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: 999px;
  padding: 2px 8px;
  background: #e2e8f0;
  color: var(--qa-text-secondary);
}

.qa-sev--high,
.qa-sev--critical {
  background: #fee2e2;
  color: #991b1b;
}

.qa-sev--medium {
  background: #ffedd5;
  color: #9a3412;
}

.qa-sev--low {
  background: #dcfce7;
  color: #166534;
}

/* Review state, in the top row beside severity. It used to sit in the grey meta
   line next to the conversation id, which ranked "nobody has looked at this"
   level with a reference number. */
.qa-live-state {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: 999px;
  padding: 2px 8px;
  white-space: nowrap;
  background: #e2e8f0;
  color: var(--qa-text-secondary);
}

.qa-live-state--pending {
  background: #fef3c7;
  color: #92400e;
}

.qa-live-state--danger {
  background: #fee2e2;
  color: #991b1b;
}

.qa-live-state--good {
  background: #dcfce7;
  color: #166534;
}

.qa-live-state--warn {
  background: #ffedd5;
  color: #9a3412;
}

.qa-live-expected-item {
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px dashed var(--qa-border-soft);
}

.qa-live-expected-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: 0;
}

.qa-live-expected-head {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.qa-live-expected-item code {
  font-size: 11px;
  color: var(--qa-accent);
}

.qa-live-attach-list {
  margin-top: 6px;
}

.qa-live-attach-item {
  font-size: 11px;
  color: var(--qa-text-muted);
  padding: 2px 0;
}

.qa-live-diff-block {
  margin-top: 10px;
}

.qa-live-diff-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--qa-text-secondary);
  margin-bottom: 4px;
}

.qa-live-messages {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.qa-msg {
  border: 1px solid var(--qa-border);
  border-radius: 12px;
  padding: 10px 12px;
  background: var(--qa-muted);
  color: var(--qa-text);
}

.qa-msg--in {
  align-self: flex-start;
  max-width: 85%;
}

.qa-msg--out {
  align-self: flex-end;
  max-width: 85%;
  background: var(--qa-msg-out-bg);
}

.qa-msg--customer {
  align-self: flex-start;
  max-width: 85%;
  border-color: #f59e0b;
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.15);
  background: var(--qa-msg-customer-bg);
}

.qa-msg--agent {
  align-self: flex-end;
  max-width: 85%;
  border-color: var(--qa-accent);
  box-shadow: 0 0 0 2px rgba(15, 118, 110, 0.12);
  background: var(--qa-accent-soft);
}

.qa-msg-role {
  margin-left: 6px;
  font-style: normal;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--qa-text-muted);
}

.qa-msg-meta {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: var(--qa-text-muted);
  margin-bottom: 4px;
}

.qa-msg-body {
  font-size: 13px;
  white-space: pre-wrap;
  line-height: 1.45;
}

.qa-live-panel {
  padding: 12px 14px;
  border-bottom: 1px solid var(--qa-border-soft);
}

.qa-live-panel--status {
  background: var(--qa-muted);
}

.qa-live-panel--actions {
  background: var(--qa-surface);
  position: sticky;
  top: 41px;
  z-index: 1;
  box-shadow: 0 1px 0 var(--qa-border-soft);
}

.qa-live-panel h3 {
  margin: 0 0 8px;
  font-size: 13px;
  color: var(--qa-text);
}

.qa-live-actions--primary {
  margin-bottom: 8px;
}

.qa-live-actions--secondary {
  gap: 6px;
}

.qa-live-actions--batch {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed var(--qa-border-soft);
}

.qa-live-kv {
  justify-content: space-between;
  font-size: 12px;
  padding: 4px 0;
  color: var(--qa-text-muted);
}

.qa-live-kv strong {
  color: var(--qa-text);
}

.qa-live-pre {
  margin: 0;
  max-height: 180px;
  overflow: auto;
  font-size: 11px;
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 8px;
  padding: 10px;
}

.qa-live-telegram,
.qa-live-timeline {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  justify-content: space-between;
  font-size: 12px;
  padding: 6px 0;
  border-bottom: 1px dashed var(--qa-border);
  color: var(--qa-text-secondary);
}

.qa-live-timeline strong {
  flex: 1 1 100%;
  color: var(--qa-text);
}

.qa-badge-status {
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  background: #e2e8f0;
  color: var(--qa-text-secondary);
}

.qa-badge-status--delivered {
  background: #dcfce7;
  color: #166534;
}

.qa-badge-status--failed {
  background: #fee2e2;
  color: #991b1b;
}

.qa-badge-status--queued {
  background: #e2e8f0;
  color: #475569;
}

@media (max-width: 1100px) {
  .qa-live-grid {
    grid-template-columns: 1fr;
  }

  .qa-live-col {
    max-height: 50vh;
    border-right: 0;
    border-bottom: 1px solid var(--qa-border);
  }

  .qa-live-panel--actions {
    position: static;
  }
}

/* Latest review outcome card under the transcript. */
.qa-live-outcome {
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid var(--qa-border);
  border-left-width: 3px;
  border-radius: 8px;
  background: var(--qa-surface);
}
.qa-live-outcome-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
}
.qa-live-outcome-head strong {
  font-size: 13px;
}
.qa-live-outcome-time {
  font-size: 11px;
  color: var(--qa-text-secondary);
  white-space: nowrap;
}
.qa-live-outcome-note {
  margin-top: 6px;
  font-size: 12px;
  color: var(--qa-text-secondary);
  white-space: pre-wrap;
  word-break: break-word;
}
.qa-live-outcome--good {
  border-left-color: var(--qa-good);
}
.qa-live-outcome--good .qa-live-outcome-head strong {
  color: var(--qa-good);
}
.qa-live-outcome--danger {
  border-left-color: var(--qa-bad);
}
.qa-live-outcome--danger .qa-live-outcome-head strong {
  color: var(--qa-bad);
}
.qa-live-outcome--warn {
  border-left-color: var(--qa-warn);
}
.qa-live-outcome--warn .qa-live-outcome-head strong {
  color: var(--qa-warn);
}
.qa-live-outcome--muted {
  border-left-color: var(--qa-border);
}
</style>

<style>
/* Live Monitor is outside qa-dashboard-wrapper.
   Dark = real dark surfaces; Mix = light content canvas.

   The :not(.theme-mix) guard is what keeps those two apart. Mix carries
   body.dark as well, and the root element here is BOTH .qa-live and .qa-page,
   so `body.dark .qa-live` and `body.theme-mix .qa-page` (qaShell.css) landed on
   the same element with the same specificity — and this file, being a component
   style, is injected last and won. The Mix block below could only undo what it
   named explicitly: the colour TOKENS below stayed dark, and all 92 var(--qa-*)
   readers in this file drew dark text on Mix's light surface. The active tab,
   the Source/Severity/Search labels and every disclosure summary were white on
   white. Guarding here lets .qa-page's Mix tokens through instead. */
body.dark:not(.theme-mix) .qa-live {
  --qa-bg: #151820;
  --qa-surface: #202024;
  --qa-muted: #252a33;
  --qa-border: rgba(255, 255, 255, 0.1);
  --qa-text: #ffffff;
  --qa-text-muted: rgba(255, 255, 255, 0.6);
  --qa-text-secondary: rgba(255, 255, 255, 0.8);
  /* Translucent over the dark card rather than flat hex, matching how the rest
     of the dark theme tints (--qa-accent-soft, .qa-live-item.is-active). Keeps
     amber = the customer message that triggered this, blue = outbound. */
  --qa-msg-customer-bg: rgba(245, 158, 11, 0.16);
  --qa-msg-out-bg: rgba(51, 136, 255, 0.16);
  background: radial-gradient(
      1200px 420px at 10% -10%,
      rgba(51, 136, 255, 0.12),
      transparent 60%
    ),
    linear-gradient(180deg, #151820 0%, #202024 40%) !important;
  color: #ffffff !important;
}

body.dark:not(.theme-mix) .qa-live .qa-live-header,
body.dark:not(.theme-mix) .qa-live .qa-live-tabs,
body.dark:not(.theme-mix) .qa-live .qa-live-filter-card,
body.dark:not(.theme-mix) .qa-live .qa-live-col,
body.dark:not(.theme-mix) .qa-live .qa-live-item,
body.dark:not(.theme-mix) .qa-live .qa-live-pager,
body.dark:not(.theme-mix) .qa-live .qa-live-panel--actions {
  background: #202024 !important;
  color: #ffffff !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

body.dark:not(.theme-mix) .qa-live .qa-live-col-head,
body.dark:not(.theme-mix) .qa-live .qa-live-panel--status,
body.dark:not(.theme-mix) .qa-live .qa-live-disclosure {
  background: #252a33 !important;
  color: #ffffff !important;
}

body.dark:not(.theme-mix) .qa-live .qa-live-title,
body.dark:not(.theme-mix) .qa-live .qa-live-item-title,
body.dark:not(.theme-mix) .qa-live .qa-live-panel h3,
body.dark:not(.theme-mix) .qa-live .qa-live-kv strong,
body.dark:not(.theme-mix) .qa-live .qa-msg-body {
  color: #ffffff !important;
}

body.dark:not(.theme-mix) .qa-live .qa-live-desc,
body.dark:not(.theme-mix) .qa-live .qa-live-empty,
body.dark:not(.theme-mix) .qa-live .qa-live-muted,
body.dark:not(.theme-mix) .qa-live .qa-live-item-summary,
body.dark:not(.theme-mix) .qa-live .qa-live-item-meta,
body.dark:not(.theme-mix) .qa-live .qa-msg-meta,
body.dark:not(.theme-mix) .qa-live .qa-live-label,
body.dark:not(.theme-mix) .qa-live .qa-live-disclosure > summary {
  color: rgba(255, 255, 255, 0.6) !important;
}

body.dark:not(.theme-mix) .qa-live .qa-live-input,
body.dark:not(.theme-mix) .qa-live .qa-live-textarea {
  background: rgba(255, 255, 255, 0.05) !important;
  color: #ffffff !important;
  border-color: rgba(255, 255, 255, 0.14) !important;
}

body.dark:not(.theme-mix) .qa-live .qa-live-tab:not(.is-active),
body.dark:not(.theme-mix) .qa-live .qa-live-btn--ghost {
  background: transparent !important;
  color: rgba(255, 255, 255, 0.8) !important;
}

body.dark:not(.theme-mix) .qa-live .qa-live-item.is-active {
  background: rgba(51, 136, 255, 0.16) !important;
}

body.theme-mix .qa-live {
  background: radial-gradient(
      1200px 420px at 10% -10%,
      rgba(14, 116, 144, 0.08),
      transparent 60%
    ),
    linear-gradient(180deg, #f8fafc 0%, #ffffff 40%) !important;
  color: #111827 !important;
}

body.theme-mix .qa-live .qa-live-header,
body.theme-mix .qa-live .qa-live-tabs,
body.theme-mix .qa-live .qa-live-filter-card,
body.theme-mix .qa-live .qa-live-col,
body.theme-mix .qa-live .qa-live-item,
body.theme-mix .qa-live .qa-live-pager,
body.theme-mix .qa-live .qa-live-panel--actions {
  background: #ffffff !important;
  color: #111827 !important;
  border-color: #e5e7eb !important;
}

body.theme-mix .qa-live .qa-live-col-head,
body.theme-mix .qa-live .qa-live-panel--status,
body.theme-mix .qa-live .qa-live-disclosure {
  background: #f8fafc !important;
  color: #111827 !important;
}

body.theme-mix .qa-live .qa-live-title,
body.theme-mix .qa-live .qa-live-item-title,
body.theme-mix .qa-live .qa-live-panel h3,
body.theme-mix .qa-live .qa-live-kv strong,
body.theme-mix .qa-live .qa-msg-body {
  color: #111827 !important;
}

body.theme-mix .qa-live .qa-live-desc,
body.theme-mix .qa-live .qa-live-empty,
body.theme-mix .qa-live .qa-live-muted,
body.theme-mix .qa-live .qa-live-item-summary,
body.theme-mix .qa-live .qa-live-item-meta,
body.theme-mix .qa-live .qa-msg-meta {
  color: #6b7280 !important;
}

body.theme-mix .qa-live .qa-live-input,
body.theme-mix .qa-live .qa-live-textarea {
  background: #ffffff !important;
  color: #111827 !important;
  border-color: #cbd5e1 !important;
}

body.theme-mix .qa-live .qa-live-tab:not(.is-active),
body.theme-mix .qa-live .qa-live-btn--ghost {
  background: transparent !important;
  color: #374151 !important;
}

body.theme-mix .qa-live .qa-live-item.is-active {
  background: #ecfeff !important;
}
</style>
